import { Component } from 'react';
import { theme } from '../../lib/theme';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * ErrorBoundary — catches JS errors in any child component tree.
 *
 * Usage:
 *   <ErrorBoundary name="Leads">
 *     <LeadsView />
 *   </ErrorBoundary>
 *
 * Props:
 *   name      — label shown in the error UI (e.g. "Leads")
 *   fallback  — optional custom fallback component
 *   onError   — optional callback(error, errorInfo)
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`[ErrorBoundary:${this.props.name || 'unknown'}]`, error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div style={{
          padding: 40,
          textAlign: 'center',
          maxWidth: 480,
          margin: '60px auto',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: `${theme.colors.danger}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <AlertTriangle size={28} color={theme.colors.danger} />
          </div>

          <h3 style={{
            color: theme.colors.text,
            fontSize: 18, fontWeight: 700,
            margin: '0 0 8px',
          }}>
            Something went wrong
          </h3>

          <p style={{
            color: theme.colors.textLight,
            fontSize: 14, lineHeight: 1.5,
            margin: '0 0 20px',
          }}>
            {this.props.name
              ? `The ${this.props.name} section encountered an error.`
              : 'An unexpected error occurred.'
            }
            {' '}Try refreshing, or contact support if the problem persists.
          </p>

          {this.state.error?.message && (
            <pre style={{
              background: theme.colors.bg,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.sm,
              padding: 12,
              fontSize: 12,
              color: theme.colors.danger,
              textAlign: 'left',
              overflow: 'auto',
              maxHeight: 120,
              margin: '0 0 20px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {this.state.error.message}
            </pre>
          )}

          <button
            onClick={this.handleRetry}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 20px', borderRadius: theme.radius.sm,
              background: theme.colors.primary, color: 'white',
              border: 'none', fontSize: 14, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
